/**
 * Marble4ApiController
 * 封装 World Labs Marble API：media-assets 上传、world 列表、world 生成与轮询
 * API 文档: https://docs.worldlabs.ai/api
 */
import { getFileName, getFileKind } from '@/utils/fileUtils';
import { ShowMsgUI, createShowMsgUI } from '@/uiControls/base/ui/showMsgUI';
import { globalConfig, modelDialogTools } from '@/utils/globalConfig';
import { getI18nText } from '@/utils/commonFunc';

// --- 类型定义 (基于 OpenAPI) ---

export type MediaAssetKind = 'image' | 'video';

export interface MediaAsset {
  media_asset_id: string;
  file_name: string;
  kind: MediaAssetKind;
  extension?: string | null;
  created_at: string;
  updated_at?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface UploadUrlInfo {
  upload_url: string;
  upload_method: string;
  required_headers?: Record<string, string> | null;
  curl_example?: string | null;
}

export interface MediaAssetPrepareUploadResponse {
  media_asset: MediaAsset;
  upload_info: UploadUrlInfo;
}

export interface WorldAssets {
  caption?: string | null;
  thumbnail_url?: string | null;
  splats?: { spz_urls?: Record<string, string> | null } | null;
  mesh?: { collider_mesh_url?: string | null } | null;
  imagery?: { pano_url?: string | null } | null;
}

export interface World {
  world_id: string;
  display_name: string;
  world_marble_url: string;
  assets?: WorldAssets | null;
  created_at?: string | null;
  updated_at?: string | null;
  model?: string | null;
  tags?: string[] | null;
  world_prompt?: unknown;
  permission?: unknown;
}

export interface ListWorldsRequest {
  page_size?: number;
  page_token?: string | null;
  status?: 'SUCCEEDED' | 'PENDING' | 'FAILED' | 'RUNNING' | null;
  model?: string | null;
  tags?: string[] | null;
  is_public?: boolean | null;
  created_after?: string | null;
  created_before?: string | null;
  sort_by?: 'created_at' | 'updated_at';
}

export interface ListWorldsResponse {
  worlds: World[];
  next_page_token?: string | null;
}

export interface GenerateWorldResponse {
  operation_id: string;
  done: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  expires_at?: string | null;
  error?: { code?: number; message?: string } | null;
  metadata?: Record<string, unknown> | null;
  response?: World | null;
}

export type WorldPromptType = 'text' | 'image' | 'multi-image' | 'video';

export interface TextWorldPrompt {
  type: 'text';
  text_prompt: string | null;
  disable_recaption?: boolean | null;
}

export interface ImageWorldPrompt {
  type: 'image';
  image_prompt: {
    source: 'uri';
    uri: string;
  } | {
    source: 'media_asset';
    media_asset_id: string;
  } | {
    source: 'data_base64';
    data_base64: string;
    extension?: string;
  };
  text_prompt?: string | null;
  is_pano?: boolean | null;
  disable_recaption?: boolean | null;
}

export interface MultiImageWorldPrompt {
  type: 'multi-image';
  multi_image_prompt: Array<{
    azimuth?: number | null;
    content: {
      source: 'uri';
      uri: string;
    } | {
      source: 'media_asset';
      media_asset_id: string;
    };
  }>;
  text_prompt?: string | null;
  reconstruct_images?: boolean;
  disable_recaption?: boolean | null;
}

export interface VideoWorldPrompt {
  type: 'video';
  video_prompt: {
    source: 'uri';
    uri: string;
  } | {
    source: 'media_asset';
    media_asset_id: string;
  };
  text_prompt?: string | null;
  disable_recaption?: boolean | null;
}

export type WorldPromptInput =
  | TextWorldPrompt
  | ImageWorldPrompt
  | MultiImageWorldPrompt
  | VideoWorldPrompt;

export interface GenerateWorldRequest {
  world_prompt: WorldPromptInput;
  display_name?: string | null;
  model?: string | null;
  tags?: string[] | null;
  seed?: number | null;
  permission?: {
    public?: boolean;
    allowed_readers?: string[];
    allowed_writers?: string[];
  } | null;
}

const azimuthList = {
  az0: { azimuth: 0, value: 0, description: 'Front' },
  az1: { azimuth: 1, value: 180, description: 'Back' },
  az2: { azimuth: 2, value: 90, description: 'Right' },
  az3: { azimuth: 3, value: 270, description: 'Left' }
};

class Marble4ApiController {
  private apiKey: string = '';
  /** 与 https://docs.worldlabs.ai/api 一致；若使用 marble4 端点可改为 marble4/v1 */
  //   private apiBaseUrl: string = 'https://api.worldlabs.ai/marble/v1';
  private apiBaseUrl: string = import.meta.env.VITE_MARBLE_API_BASE_URL;
  private showMsgUI: ShowMsgUI | null = null;

  setApiKey(key: string): void {
    this.apiKey = key;
    globalConfig.MARBLE_API_KEY = key;
  }

  getApiKey(): string {
    return this.apiKey;
  }

  setApiBaseUrl(url: string): void {
    this.apiBaseUrl = url.replace(/\/$/, '');
  }

  public async generateMarbleModel(name: string, textPrompt: string, filesList: any, autoEnhance: boolean, model: string) {
    // 3. 
    // 4. get world status
    // 5. ok,return world info
    const tempId = Date.now().toString();
    let mediaInfo = {
      tempId: tempId,
      hasmedia: false,
      kind: '',
      media_asset_ids: [] as { id: string, azimuth: number }[],
    }
    // 1. prepare upload,upload media assets
    if (filesList && filesList.length > 0) {
      modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.uploadingMediaAssets'));
      for (const [index, file] of filesList.entries()) {
        try {
          const file_name = getFileName(file.file_name);
          const mediaAssetId = await this.uploadMediaAsset(file_name, file.kind, file.file, { extension: file.extension });
          mediaInfo.kind = file.kind;
          const azimuth = azimuthList['az' + file.azimuth] || azimuthList.az0;
          mediaInfo.media_asset_ids.push({ id: mediaAssetId, azimuth: azimuth.value });
          mediaInfo.hasmedia = true;
          if (index === 0) { // 做出上传等待效果
            modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.generatingMarbleModel'));
            modelDialogTools.closeLoadingInfoModal(3);
          }
        } catch (error) {
          console.error("upload error----", error);
          if (!this.showMsgUI) {
            this.showMsgUI = createShowMsgUI();
          }
          this.showMsgUI.showInfo(getI18nText('errorinfo.marbleModelGenerateFailed') + (error as Error).message, 3);
          // 增加方法，更新场景管理列表----------------------------------helen 2026-04-16
          window.dispatchEvent(new CustomEvent('event.marbleModelResponse', {
            detail: { tempId: tempId, status: 'failed', name: name }
          }));
          return null;
        }
      }
    }
    try {
      // 2. generate world，get world id
      window.dispatchEvent(new CustomEvent('event.marbleModelResponse', {
        detail: { tempId: tempId, status: 'create', name: name }
      }));
      const worldPrompt = this.getWorldPrompt(textPrompt, mediaInfo);    // if(mediaInfo.hasmedia){
      const operationId = await this.generateWorld({
        world_prompt: worldPrompt,
        display_name: name,
        model: model
      });
      window.dispatchEvent(new CustomEvent('event.marbleModelResponse', {
        detail: { tempId: tempId, worldid: operationId, status: 'generating', name: name }
      }));

      // 3. get world status
      this.waitForWorld(operationId, 1000);
    } catch (error) {
      console.error("generate world error----", (error as Error).message);
      if (!this.showMsgUI) {
        this.showMsgUI = createShowMsgUI();
      }
      this.showMsgUI.showInfo(getI18nText('errorinfo.marbleModelGenerateFailed') + (error as Error).message, 30);
      window.dispatchEvent(new CustomEvent('event.marbleModelResponse', {
        detail: { tempId: tempId, status: 'failed', name: name }
      }));
      return null;
    }
    return null;
  }

  private async apiFetch<T = unknown>(
    path: string,
    options: { method?: string; headers?: Record<string, string>; body?: unknown } = {}
  ): Promise<T> {
    if (!this.apiKey&&!options.headers?.['WLT-Api-Key']) {
      throw new Error('请先设置 API Key (setApiKey)。');
    }

    const { body, method = 'GET', headers: optHeaders = {} } = options;
    const headers: Record<string, string> = {
      'WLT-Api-Key': options.headers?.['WLT-Api-Key'] || this.apiKey,
      'Content-Type': 'application/json',
      ...optHeaders,
    };

    const response = await fetch(`${this.apiBaseUrl}/${path.replace(/^\//, '')}`, {
      method,
      headers,
      body: body !== undefined ? (typeof body === 'string' ? body : JSON.stringify(body)) : undefined,
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const msg = typeof data?.detail === 'string' ? data.detail : JSON.stringify(data);
      throw new Error(`${path}: ${response.status} ${msg}`);
    }
    return data as T;
  }

  private getWorldPrompt(textPrompt: string, mediaInfo: any): WorldPromptInput {
    let result: WorldPromptInput = { type: 'text', text_prompt: textPrompt };
    if (mediaInfo.hasmedia) {
      if (mediaInfo.kind === 'image') {
        if (mediaInfo.media_asset_ids.length > 1) {
          result.type = 'multi-image';
          result.multi_image_prompt = mediaInfo.media_asset_ids.map(item => ({ azimuth: item.azimuth, content: { source: 'media_asset', media_asset_id: item.id } }));
        } else {
          result.type = 'image';
          result.image_prompt = { source: 'media_asset', media_asset_id: mediaInfo.media_asset_ids[0].id };
        }
        // result = { type: 'image', image_prompt: { source: 'media_asset', media_asset_id: mediaInfo.media_asset_ids[0].id } };
      } else if (mediaInfo.kind === 'video') {
        result.type = 'video';
        result.video_prompt = { source: 'media_asset', media_asset_id: mediaInfo.media_asset_ids[0].id };
      }
    }
    return result;
  }

  // ========== Media Assets ==========

  /**
   * 准备上传：获取 media_asset_id 和签名上传 URL
   * @param file_name 文件名，如 "landscape.jpg"
   * @param kind "image" | "video"
   * @param extension 扩展名不含点，如 "jpg", "mp4"
   * @param metadata 可选元数据
   */
  async prepareMediaUpload(
    file_name: string,
    kind: MediaAssetKind,
    extension?: string,
    metadata?: Record<string, unknown>
  ): Promise<MediaAssetPrepareUploadResponse> {
    const ext = extension ?? file_name.split('.').pop() ?? '';
    return this.apiFetch<MediaAssetPrepareUploadResponse>('media-assets:prepare_upload', {
      method: 'POST',
      body: {
        file_name,
        kind,
        ...(ext && { extension: ext.replace(/^\./, '') }),
        ...(metadata && { metadata }),
      },
    });
  }

  /**
   * 使用 prepare 返回的 upload_url 上传文件内容
   * @param uploadUrl 来自 prepareMediaUpload 的 upload_info.upload_url
   * @param fileBody 文件二进制（Blob / ArrayBuffer / ReadableStream 等）
   * @param requiredHeaders 来自 upload_info.required_headers，需包含 Content-Type 等
   */
  async uploadToSignedUrl(
    uploadUrl: string,
    file: File,
    requiredHeaders?: Record<string, string> | null
  ): Promise<void> {
    const headers: Record<string, string> = {
      ...(requiredHeaders ?? {}),
    };
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/octet-stream';
    }
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers,
      body: file,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Upload failed: ${response.status} ${text}`);
    }
  }

  /**
   * 一站式：准备上传 + 上传字节，返回 media_asset_id
   */
  async uploadMediaAsset(
    file_name: string,
    kind: MediaAssetKind,
    file: File,
    options?: { extension?: string; metadata?: Record<string, unknown> }
  ): Promise<string> {
    const { media_asset, upload_info } = await this.prepareMediaUpload(
      file_name,
      kind,
      options?.extension,
      options?.metadata
    );
    await this.uploadToSignedUrl(
      upload_info.upload_url,
      file,
      upload_info.required_headers ?? undefined
    );

    return media_asset.media_asset_id;
  }

  // ========== Worlds: List & Get ==========

  /**
   * 获取 world 列表（分页、筛选）
   */
  async listWorlds(params: ListWorldsRequest = {}): Promise<ListWorldsResponse> {
    return this.apiFetch<ListWorldsResponse>('worlds:list', {
      method: 'POST',
      body: {
        page_size: params.page_size ?? 5,
        page_token: params.page_token ?? undefined,
        status: params.status ?? undefined,
        model: params.model ?? undefined,
        tags: params.tags ?? undefined,
        is_public: params.is_public ?? undefined,
        created_after: params.created_after ?? undefined,
        created_before: params.created_before ?? undefined,
        sort_by: params.sort_by ?? 'created_at',
      },
    });
  }

  /**
   * 根据 world_id 获取单个 world 详情
   */
  async getWorld(worldId: string): Promise<World> {
    return this.apiFetch<World>(`worlds/${encodeURIComponent(worldId)}`, {
      method: 'GET',
    });
  }

  // ========== Worlds: Generate & Operations ==========

  /**
   * 发起 world 生成，返回 operation_id
   */
  async generateWorld(request: GenerateWorldRequest): Promise<string> {
    const res = await this.apiFetch<GenerateWorldResponse>('worlds:generate', {
      method: 'POST',
      body: {
        world_prompt: request.world_prompt,
        display_name: request.display_name ?? undefined,
        model: request.model ?? 'Marble 0.1-plus',
        tags: request.tags ?? undefined,
        seed: request.seed ?? undefined,
        permission: request.permission ?? undefined,
      },
    });
    return res.operation_id;
  }

  /**
   * 轮询操作状态，返回操作结果（含 response 中的 World）
   */
  async getOperation(operationId: string): Promise<GenerateWorldResponse> {
    return this.apiFetch<GenerateWorldResponse>(
      `operations/${encodeURIComponent(operationId)}`,
      { method: 'GET' }
    );
  }

  /**
   * 轮询直到完成，返回生成的 World；若失败则抛错
   * @param operationId 来自 generateWorld 的 operation_id
   * @param pollIntervalMs 轮询间隔（毫秒）
   */
  async waitForWorld(
    operationId: string,
    pollIntervalMs: number = 5000
  ): Promise<World> {
    for (; ;) {
      const op = await this.getOperation(operationId);
      if (op.error) {
        throw new Error(
          `World generation failed: ${op.error.message ?? JSON.stringify(op.error)}`
        );
      }
      if (op.done && op.response) {
        const modelUrl = op.response.assets.splats.spz_urls.full_res;
        if (modelUrl) {
          window.dispatchEvent(new CustomEvent('event.marbleModelResponse', {
            detail: { operationId: op.operation_id, worldid: op.response.world_id, status: 'success', modelUrl: modelUrl }
          }));
          return op.response;
        } else {
          window.dispatchEvent(new CustomEvent('event.marbleModelResponse', {
            detail: { operationId: op.operation_id, worldid: op.response.world_id, status: 'failed', modelUrl: '' }
          }));
          throw new Error("world generated failed");
        }
      }
      await new Promise((r) => setTimeout(r, pollIntervalMs));
    }
  }

  async checkMarbleKeyAvailable(apiKey: string): Promise<boolean> {
    try {
      console.log("检查apiKey是否有效----", apiKey);
      const res = await this.apiFetch<{ available: boolean }>('worlds:list', {
        method: 'POST',
        headers:{'WLT-Api-Key': apiKey},
        body: { api_key: apiKey },
      });
      console.log("检查apiKey是否有效--结果--", res);
      return true;
    } catch (error) {
      console.error("check MarbleKey Available error----", error);
      return false;
    }

  }

  checkMarbleModelAsset(assets: WorldAssets): boolean {
    if (!assets) {
      return false;
    }
    if (assets.thumbnail_url && assets.splats?.spz_urls?.full_res) {
      return true;
    }
    return false;
  }

}

const marble4ApiController = new Marble4ApiController();
export default marble4ApiController;